/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useRef, useState } from "react";

const NEXT_KEY = { horizontal: "ArrowRight", vertical: "ArrowDown" };
const PREV_KEY = { horizontal: "ArrowLeft", vertical: "ArrowUp" };

/**
 * Roving-tabindex focus for a composite widget (tablist, radiogroup, toolbar):
 * one item in the tab order at a time, Arrow keys (per `orientation`) + Home/End
 * move focus. Navigation only — components keep their own activation.
 *
 * Call with item `count`, spread `getItemProps(index)` (`{ ref, tabIndex,
 * onKeyDown }`) onto each item. `defaultIndex` is the initially-tabbable item.
 */
export const useRovingTabIndex = ({ count, orientation = "horizontal", loop = true, defaultIndex = 0 }) => {
  const itemsRef = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(defaultIndex);

  const focusIndex = useCallback(
    (index) => {
      if (count <= 0) return;
      let next = index;
      if (next < 0) next = loop ? count - 1 : 0;
      if (next >= count) next = loop ? 0 : count - 1;
      setFocusedIndex(next);
      itemsRef.current[next]?.focus();
    },
    [count, loop]
  );

  const handleKeyDown = useCallback(
    (event, index) => {
      if (event.key === NEXT_KEY[orientation]) {
        event.preventDefault();
        focusIndex(index + 1);
      } else if (event.key === PREV_KEY[orientation]) {
        event.preventDefault();
        focusIndex(index - 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        focusIndex(0);
      } else if (event.key === "End") {
        event.preventDefault();
        focusIndex(count - 1);
      }
    },
    [orientation, focusIndex, count]
  );

  const getItemProps = useCallback(
    (index) => ({
      ref: (el) => {
        itemsRef.current[index] = el;
      },
      tabIndex: index === focusedIndex ? 0 : -1,
      onKeyDown: (event) => handleKeyDown(event, index),
    }),
    [focusedIndex, handleKeyDown]
  );

  return { getItemProps, focusedIndex, setFocusedIndex };
};
